import { z } from 'zod';
/**
 * Codice Fiscale italiano — validation.
 *
 * Supports:
 *  - Persona Fisica (PF): 16 caratteri alfanumerici con checksum.
 *  - Persona Giuridica (PG): 11 cifre numeriche (coincide con Partita IVA)
 *    con checksum MOD 11 pesato (Luhn-like italiano).
 *
 * Note: questo schema è focalizzato sulla correttezza algoritmica del codice,
 * non verifica l'esistenza anagrafica (Agenzia delle Entrate).
 */
/* -------------------------------------------------------------------------- */
/* PF (persona fisica) — 16 char                                              */
/* -------------------------------------------------------------------------- */
const PF_REGEX = /^[A-Z]{6}[0-9]{2}[A-EHLMPRST][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;
// Tabelle ufficiali checksum CF PF
const ODD_MAP = {
    '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
    A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21,
    K: 2, L: 4, M: 18, N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14,
    U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23,
};
const EVEN_MAP = {
    '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
    A: 0, B: 1, C: 2, D: 3, E: 4, F: 5, G: 6, H: 7, I: 8, J: 9,
    K: 10, L: 11, M: 12, N: 13, O: 14, P: 15, Q: 16, R: 17, S: 18, T: 19,
    U: 20, V: 21, W: 22, X: 23, Y: 24, Z: 25,
};
const CHECK_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
/** Calcola la lettera di controllo per un CF PF di 15 caratteri. */
export function computeCodiceFiscaleCheckChar(first15) {
    let sum = 0;
    for (let i = 0; i < 15; i++) {
        const ch = first15[i];
        // posizione 1..15, dispari/pari secondo convenzione italiana:
        // indice 0 => posizione 1 (dispari), indice 1 => posizione 2 (pari), ...
        const isOddPosition = i % 2 === 0;
        const value = isOddPosition ? ODD_MAP[ch] : EVEN_MAP[ch];
        if (value === undefined) {
            throw new Error(`Invalid character in CF: '${ch}'`);
        }
        sum += value;
    }
    return CHECK_LETTERS[sum % 26];
}
function isValidCodiceFiscalePF(cf) {
    if (!PF_REGEX.test(cf))
        return false;
    try {
        const expected = computeCodiceFiscaleCheckChar(cf.slice(0, 15));
        return expected === cf[15];
    }
    catch {
        return false;
    }
}
/* -------------------------------------------------------------------------- */
/* PG (persona giuridica) — 11 cifre, checksum MOD 11 (come Partita IVA)       */
/* -------------------------------------------------------------------------- */
const PG_REGEX = /^[0-9]{11}$/;
function isValidCodiceFiscalePG(cf) {
    if (!PG_REGEX.test(cf))
        return false;
    let sum = 0;
    for (let i = 0; i < 10; i++) {
        let n = Number(cf[i]);
        if (i % 2 === 1) {
            // posizione pari (indice dispari 1,3,5,7,9) => raddoppia
            n *= 2;
            if (n > 9)
                n -= 9;
        }
        sum += n;
    }
    const check = (10 - (sum % 10)) % 10;
    return check === Number(cf[10]);
}
/**
 * Parse/normalize/validate un codice fiscale (PF o PG).
 */
export function parseCodiceFiscale(raw) {
    const normalized = raw.trim().toUpperCase().replace(/\s+/g, '');
    if (normalized.length === 16) {
        if (!PF_REGEX.test(normalized)) {
            return { type: 'PF', valid: false, reason: 'Formato PF non valido', normalized };
        }
        if (!isValidCodiceFiscalePF(normalized)) {
            return { type: 'PF', valid: false, reason: 'Checksum PF non valido', normalized };
        }
        return { type: 'PF', valid: true, normalized };
    }
    if (normalized.length === 11) {
        if (!PG_REGEX.test(normalized)) {
            return { type: 'PG', valid: false, reason: 'CF PG deve contenere 11 cifre', normalized };
        }
        if (!isValidCodiceFiscalePG(normalized)) {
            return { type: 'PG', valid: false, reason: 'Checksum PG (P.IVA) non valido', normalized };
        }
        return { type: 'PG', valid: true, normalized };
    }
    return {
        type: null,
        valid: false,
        reason: `Lunghezza non valida (${normalized.length}); attesi 16 (PF) o 11 (PG)`,
        normalized,
    };
}
/**
 * Zod schema per codice fiscale (PF o PG).
 * L'output è il CF normalizzato (uppercase, trimmed).
 */
export const codiceFiscaleSchema = z
    .string({ required_error: 'Il codice fiscale è obbligatorio' })
    .trim()
    .min(1, { message: 'Il codice fiscale è obbligatorio' })
    .transform((raw) => raw.toUpperCase().replace(/\s+/g, ''))
    .superRefine((cf, ctx) => {
    const parsed = parseCodiceFiscale(cf);
    if (!parsed.valid) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: parsed.reason ?? 'Codice fiscale non valido',
        });
    }
});
/** Schema ristretto solo PF. */
export const codiceFiscalePFSchema = z
    .string()
    .trim()
    .transform((raw) => raw.toUpperCase().replace(/\s+/g, ''))
    .superRefine((cf, ctx) => {
    const parsed = parseCodiceFiscale(cf);
    if (parsed.type !== 'PF' || !parsed.valid) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: parsed.reason ?? 'Codice fiscale PF non valido',
        });
    }
});
/** Schema ristretto solo PG. */
export const codiceFiscalePGSchema = z
    .string()
    .trim()
    .transform((raw) => raw.toUpperCase().replace(/\s+/g, ''))
    .superRefine((cf, ctx) => {
    const parsed = parseCodiceFiscale(cf);
    if (parsed.type !== 'PG' || !parsed.valid) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: parsed.reason ?? 'Codice fiscale PG non valido',
        });
    }
});
//# sourceMappingURL=codiceFiscale.js.map